/**
 * Tab Audit Section - Congruent Testing System
 * 
 * Philosophy: If test passes = real system works. If test fails = real system broken.
 * No false positives/negatives.
 * 
 * This component runs comprehensive audits of each tab's functionality.
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  CheckCircle2, 
  XCircle, 
  AlertTriangle, 
  Play,
  ChevronDown,
  ChevronUp,
  FileText,
  Shield,
  Database,
  Clock,
  Loader2
} from 'lucide-react';
import { API_URL } from '../../config/api';

const TabAuditSection = () => {
  const [auditResults, setAuditResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedTabs, setExpandedTabs] = useState({});
  const [lastRunTime, setLastRunTime] = useState(null);
  const [bookingFlowResults, setBookingFlowResults] = useState(null);

  const runAudit = async () => {
    setLoading(true);
    try {
      // Run both tab audit and booking data flow tests in parallel
      const [tabResponse, bookingResponse] = await Promise.all([
        fetch(`${API_URL}/api/health/tests/tab-audit/all`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' }
        }),
        fetch(`${API_URL}/api/health/tests/booking-data-flow`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' }
        })
      ]);
      
      if (!tabResponse.ok) {
        throw new Error('Tab audit failed');
      }
      
      const tabData = await tabResponse.json();
      setAuditResults(tabData);
      
      if (bookingResponse.ok) {
        const bookingData = await bookingResponse.json();
        setBookingFlowResults(bookingData);
      }
      
      setLastRunTime(new Date().toLocaleTimeString());
    } catch (error) {
      console.error('Tab audit error:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleTab = (tabName) => {
    setExpandedTabs(prev => ({
      ...prev,
      [tabName]: !prev[tabName]
    }));
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'pass': return <CheckCircle2 className="w-4 h-4 text-green-600" />;
      case 'fail': return <XCircle className="w-4 h-4 text-red-600" />;
      case 'warning': return <AlertTriangle className="w-4 h-4 text-yellow-600" />;
      default: return <AlertTriangle className="w-4 h-4 text-gray-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'pass': return 'bg-green-100 text-green-800 border-green-300';
      case 'fail': return 'bg-red-100 text-red-800 border-red-300';
      default: return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    }
  };

  return (
    <Card className="border border-indigo-200">
      <CardHeader className="bg-gradient-to-r from-indigo-50 to-purple-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-indigo-600 flex items-center justify-center">
              <FileText className="w-5 h-5 text-white" />
            </div>
            <div>
              <CardTitle className="text-lg">📜 Tab Audit Framework</CardTitle>
              <p className="text-sm text-gray-500">Congruent Tests - If test passes, system works. If test fails, system broken.</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {lastRunTime && (
              <span className="text-xs text-gray-500 flex items-center gap-1">
                <Clock className="w-3 h-3" /> Last run: {lastRunTime}
              </span>
            )}
            <Button 
              onClick={runAudit} 
              disabled={loading}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Play className="w-4 h-4 mr-2" />
              )}
              Run Tab Audit
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="pt-4 space-y-4">
        {/* Overall Score */}
        {auditResults && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 p-4 bg-gray-50 rounded-lg">
            <div className="text-center">
              <div className="text-3xl font-bold text-indigo-600">
                {auditResults.overall.percentage}%
              </div>
              <div className="text-xs text-gray-500">Health Score</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-green-600">
                {auditResults.overall.passed}
              </div>
              <div className="text-xs text-gray-500">Passed</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-red-600">
                {auditResults.overall.failed}
              </div>
              <div className="text-xs text-gray-500">Failed</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-yellow-600">
                {auditResults.overall.warnings}
              </div>
              <div className="text-xs text-gray-500">Warnings</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-gray-600">
                {auditResults.duration_ms}ms
              </div>
              <div className="text-xs text-gray-500">Duration</div>
            </div>
          </div>
        )}

        {/* Tab Results */}
        {auditResults && Object.entries(auditResults.tabs || {}).map(([tabName, tabData]) => {
          const isExpanded = expandedTabs[tabName];
          const tabTests = auditResults.all_tests?.filter(t => 
            t.category?.includes(tabName.toLowerCase()) || 
            tabName.toLowerCase().includes(t.category?.toLowerCase() || '')
          ) || [];
          
          return (
            <div 
              key={tabName}
              className={`border rounded-lg overflow-hidden ${
                tabData.status === 'pass' ? 'border-green-200' : 
                tabData.status === 'fail' ? 'border-red-200' : 'border-yellow-200'
              }`}
            >
              <div 
                className={`flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50 ${
                  tabData.status === 'pass' ? 'bg-green-50' : 
                  tabData.status === 'fail' ? 'bg-red-50' : 'bg-yellow-50'
                }`}
                onClick={() => toggleTab(tabName)}
              >
                <div className="flex items-center gap-3">
                  {getStatusIcon(tabData.status)}
                  <div>
                    <span className="font-medium capitalize">{tabName.replace(/_/g, ' ')}</span>
                    <span className="text-xs text-gray-500 ml-2">
                      ({tabData.passed} passed, {tabData.failed} failed, {tabData.warnings} warnings)
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={getStatusColor(tabData.status)}>
                    {tabData.percentage || Math.round((tabData.passed / (tabData.passed + tabData.failed + tabData.warnings)) * 100) || 0}%
                  </Badge>
                  {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </div>
              </div>
              
              {isExpanded && tabTests.length > 0 && (
                <div className="p-3 bg-white border-t space-y-2">
                  {tabTests.map((test, idx) => (
                    <div 
                      key={idx}
                      className="flex items-start gap-2 text-sm p-2 rounded bg-gray-50"
                    >
                      {getStatusIcon(test.status)}
                      <div className="flex-1">
                        <div className="font-medium">{test.name}</div>
                        <div className="text-xs text-gray-500">{test.message}</div>
                        {test.category && (
                          <Badge variant="outline" className="text-xs mt-1">
                            {test.category}
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {/* E2E Booking Data Flow Audit */}
        {bookingFlowResults && (
          <div className="mt-4 border border-blue-200 rounded-lg overflow-hidden">
            <div className="bg-blue-50 p-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Database className="w-5 h-5 text-blue-600" />
                <div>
                  <span className="font-medium text-blue-800">📜 E2E Booking Data Flow</span>
                  <span className="text-xs text-blue-600 ml-2">Fundamental Accuracy Tests</span>
                </div>
              </div>
              <Badge className={bookingFlowResults.status === 'pass' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                {bookingFlowResults.health_score}% Health
              </Badge>
            </div>
            <div className="p-3 bg-white space-y-2">
              {bookingFlowResults.tests?.map((test, idx) => (
                <div 
                  key={idx}
                  className="flex items-start gap-2 text-sm p-2 rounded bg-gray-50"
                >
                  {getStatusIcon(test.status)}
                  <div className="flex-1">
                    <div className="font-medium">{test.name}</div>
                    <div className="text-xs text-gray-500">{test.message}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Checklist Coverage */}
        {auditResults?.checklist_coverage && (
          <div className="mt-4 p-4 bg-indigo-50 rounded-lg">
            <h4 className="font-medium text-indigo-800 mb-2 flex items-center gap-2">
              <Shield className="w-4 h-4" />
              Audit Checklist Coverage
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
              {Object.entries(auditResults.checklist_coverage).map(([key, value]) => (
                <div key={key} className="flex items-center gap-2">
                  <span className="text-gray-600 capitalize">{key.replace(/_/g, ' ')}:</span>
                  <span>{value}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* No Results Yet */}
        {!auditResults && !loading && (
          <div className="text-center py-8 text-gray-500">
            <Database className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>Click "Run Tab Audit" to start comprehensive system testing</p>
            <p className="text-xs mt-1">Tests booking data integrity, fees, UTC times, and more</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TabAuditSection;
